import React, {
  Component,
  CSSProperties,
  FC,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

interface CheckBoxProps {
  id?: string;
  className?: string;
  isChecked: boolean;
  onToggle?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const CheckBox: FC<CheckBoxProps> = (props) => {
  return (
    <label className={classNames('checkbox', props.className)}>
      <input
        id={props.id || undefined}
        checked={!!props.isChecked}
        onChange={props.onToggle}
        type='checkbox'
      />
      <span className='checkbox__check'></span>
      <span>{props.children}</span>
    </label>
  );
};

interface ButtonProps {
  id?: string;
  label?: string;
  type: 'link' | 'ghost' | 'flat' | 'success' | 'primary' | 'secondary' | 'colored';
  selector?: string;
  className?: string;
  formButton?: boolean;
  disabled?: boolean;
  onRipple?: (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) => void;
}

export class Button extends Component<ButtonProps> {
  private elem: RefObject<HTMLButtonElement | HTMLDivElement>;

  constructor(props: ButtonProps) {
    super(props);
    this.elem = React.createRef();
    this.onclickHandle = this.onclickHandle.bind(this);
  }

  onclickHandle(e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) {
    const { onRipple, disabled } = this.props;

    if (disabled) return;
    const ripple = document.createElement('span');

    ripple.classList.add('ripple');
    ripple.style.top = e.nativeEvent.offsetY.toString() + 'px';
    ripple.style.left = e.nativeEvent.offsetX.toString() + 'px';

    this.elem.current?.append(ripple);

    setTimeout(() => {
      ripple.remove();
      if (onRipple) {
        onRipple(e);
      }
    }, 300);
  }

  render() {
    const { className, type, selector, label, formButton, id, disabled } = this.props;
    const cls = classNames('btn', `btn-${type}`, className, selector, { 'btn-disabled': disabled });

    if (formButton) {
      return (
        <button
          id={id}
          className={cls}
          onClick={this.onclickHandle}
          ref={this.elem as RefObject<HTMLButtonElement>}
          disabled={disabled}
        >
          {label || this.props.children}
        </button>
      );
    } else {
      return (
        <div
          id={id}
          className={cls}
          onClick={this.onclickHandle}
          ref={this.elem as RefObject<HTMLDivElement>}
        >
          {label || this.props.children}
        </div>
      );
    }
  }
}

interface TooltipProps {
  label: string;
  charLimit: number;
}

interface TooltipState {
  ishovered: boolean;
}

export class Tooltip extends Component<TooltipProps, TooltipState> {
  private tooltip: RefObject<any>;

  constructor(props: TooltipProps) {
    super(props);
    this.state = {
      ishovered: false,
    };
    this.tooltip = React.createRef();
    this.mouseenter = this.mouseenter.bind(this);
    this.mouseleave = this.mouseleave.bind(this);
  }

  mouseleave = () => {
    this.setState({ ishovered: false });
  };

  mouseenter = () => {
    this.setState({ ishovered: true });
  };

  render() {
    const { label, charLimit } = this.props;

    return (
      <div className='tooltip__wrap'>
        <p onMouseEnter={this.mouseenter} onMouseLeave={this.mouseleave}>
          {this.props.children
            ? this.props.children
            : label.length <= charLimit
            ? label
            : label.substr(0, charLimit) + '...'}
        </p>
        {label.length > charLimit ? (
          <span className={`tooltip ${this.state.ishovered ? 'show' : ''}`}>{label}</span>
        ) : null}
      </div>
    );
  }
}

export const LoaderSpinner: FC<{ style?: CSSProperties }> = ({ style }) => {
  return <div className='loader-spinner' style={style} />;
};

export const Loader: FC = () => {
  return (
    <div className='loader'>
      <LoaderSpinner />
    </div>
  );
};

interface TextFieldProps {
  placeholder?: string;
  selector?: string;
  wrapperClassName?: string;
  type?: string;
  style?: CSSProperties;
  min?: string | number;
  max?: string | number;
  value?: string | number | string[];
  hasError?: boolean;
  maxLength?: number;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onchange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onkeydown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      placeholder,
      selector,
      wrapperClassName,
      type,
      style,
      min,
      max,
      value,
      onchange,
      hasError,
      onkeydown,
      maxLength,
      onFocus,
    },
    ref
  ) => {
    return (
      <div className={classNames('text-field__wrapper', wrapperClassName)} style={style}>
        <input
          ref={ref}
          className={classNames('text-field__input', selector, {
            'has-error': hasError,
          })}
          placeholder={placeholder}
          type={type}
          min={min}
          max={max}
          onChange={onchange}
          onKeyDown={onkeydown}
          value={value}
          maxLength={maxLength}
          onFocus={onFocus}
        />
        <div className='text-field__border' />
      </div>
    );
  }
);

/**
 * Sets caret position inside input elements.
 * This does not work inside focus event in safari.
 * @see https://stackoverflow.com/a/512542/9709887
 * @param elem input dom reference
 * @param caretPos caret position from left, start with 0
 */
function setCaretPosition(elem: HTMLInputElement | undefined | null, caretPos: number) {
  if (elem != null) {
    if ((elem as any).createTextRange) {
      const range = (elem as any).createTextRange();
      range.move('character', caretPos);
      range.select();
    } else {
      if (elem.selectionStart) {
        elem.focus();
        elem.setSelectionRange(caretPos, caretPos);
      } else {
        elem.focus();
      }
    }
  }
}

interface PercentInputProps extends Omit<TextFieldProps, 'onchange'> {
  isRequired?: boolean;
  initValue?: number | undefined;
  onchange(v: number): void;
}

export const PercentInput: FC<PercentInputProps> = ({ initValue, onchange, isRequired, ...props }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState((initValue || 0).toString());
  useEffect(() => setValue((initValue || 0).toString()), [initValue]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, _prev: string) => {
      const rawValue = e.target.value;
      const input = rawValue.replace('%', '');
      const value = parseFloat(input);

      if (isNaN(value) || value < 0) {
        onchange(0);
        setValue('0');
        setTimeout(() => setCaretPosition(ref.current, 1));
        return;
      }

      if (value > 100) {
        onchange(100);
        setValue('100');
        setTimeout(() => setCaretPosition(ref.current, 3));
        return;
      }

      onchange(value);
      setValue(input.slice(-1) === '.' ? input : value.toString());

      if (value < 10) {
        setTimeout(() => setCaretPosition(ref.current, 1));
      }
    },
    [onchange]
  );

  return (
    <TextField
      {...props}
      ref={ref}
      type='text'
      hasError={props.hasError || (isRequired && !value)}
      value={`${value}%`}
      onchange={(e) => onChange(e, value)}
    />
  );
};

// export const TextField = (props: Props): View =>
//   h(TextFieldC, <any>props);
